import { ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getBillingStatus,
  startTrial,
  startBillingPortal,
  chooseFreeTier,
} from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { Check, Gift } from "lucide-react";
import { ReferralModal } from "@/components/ReferralModal";
import { ContactUsModal } from "@/components/ContactUsModal";
import HelpTooltip from "@/components/shared/HelpTooltip";
import { T, useT, type TranslationKey } from "@/lib/translation";
import FreeTierPortfolioPicker from "./FreeTierPortfolioPicker";
import { useLocale } from "@/hooks/useLocale";
import { formatFixed } from "@/lib/locale";
import {
  formatSaleEndDate,
  getPlanPrices,
  getReferralCredit,
} from "@/lib/pricing";
import { PlanOption } from "@/components/billing/PlanOption";
import { SaleBadge } from "@/components/billing/SaleBadge";
import { DEFAULT_ACCENT_COLOR } from "@/lib/accentColor";

type Plan = "monthly" | "yearly";

const FREE_FEATURE_KEYS: TranslationKey[] = [
  "billing:choice.freeFeatures.portfolios",
  "billing:choice.freeFeatures.csvImports",
  "billing:choice.freeFeatures.performance",
  "billing:choice.freeFeatures.dividends",
  "billing:choice.freeFeatures.watchlist",
  "billing:choice.freeFeatures.annualFigures",
];

const FULL_FEATURE_KEYS: TranslationKey[] = [
  "billing:choice.fullFeatures.everythingInFree",
  "billing:choice.fullFeatures.customWatchlists",
  "billing:choice.fullFeatures.brokerSync",
  "billing:choice.fullFeatures.financialData",
  "billing:choice.fullFeatures.transcripts",
  "billing:choice.fullFeatures.dcfAndCharts",
  "billing:choice.fullFeatures.earningsNewsTax",
];

// Trigger for the "contact support" sentence; <T> fills in the translated link text.
function ContactSupportLink({ children }: { children?: ReactNode }) {
  return (
    <ContactUsModal mode="auth">
      <button className="text-primary underline underline-offset-2 hover:no-underline">
        {children}
      </button>
    </ContactUsModal>
  );
}

function ChoiceOption({
  label,
  price,
  priceNote,
  saleBadge,
  features,
  selected,
  onSelect,
}: {
  label: string;
  price: string;
  priceNote: string;
  saleBadge?: string;
  features: string[];
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`relative flex flex-1 flex-col items-stretch justify-start rounded-xl border p-4 text-left transition-colors ${
        saleBadge ? "border-2 " : ""
      }${
        selected
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "border-border hover:border-primary/40"
      }`}
      style={saleBadge ? { borderColor: DEFAULT_ACCENT_COLOR } : undefined}
    >
      {/* Shared minimum height so the feature lists start on the same line in both cards. */}
      <div className="min-h-[4.25rem]">
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground mb-1">
          {label}
        </p>
        <p className="text-2xl font-bold tracking-tight text-foreground">
          {price}
        </p>
        <p className="text-xs text-muted-foreground mt-1">{priceNote}</p>
      </div>
      <ul className="mt-3 space-y-2 border-t pt-3">
        {features.map((feature) => (
          <li
            key={feature}
            className="flex items-start gap-2 text-sm text-foreground/80 leading-relaxed"
          >
            <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      {/* Last in the DOM so the card's accessible name still starts with its label. */}
      {saleBadge && (
        <SaleBadge className="absolute -top-3 right-3">{saleBadge}</SaleBadge>
      )}
    </button>
  );
}

type Props = {
  children: ReactNode;
  /** Optional: treat only 'active' as fully paid (trialing allowed by default) */
  requireActive?: boolean;
};

/**
 * BillingGate — decides what a user sees once their trial has ended.
 * Controlled by VITE_ENABLE_BILLING_GATING (default off for closed beta).
 *
 * With the free tier on (backend `free_tier_enabled`), an expired trial is no longer a
 * lockout: the user is asked once to pick the free plan or subscribe, and after that choice
 * the gate steps aside permanently. Free-tier limits are then applied per feature, not here.
 * With the free tier off, this keeps the original hard-lockout behaviour.
 *
 * Allowed straight through: 'trialing', 'active', or still within trial_ends_at.
 * Friends & family (is_ff) are always allowed.
 *
 * Shares the "billing-info-status" query key with BillingInfoModal so both
 * components read from a single deduplicated fetch instead of firing two
 * separate /me/billing calls on every authenticated page load.
 *
 * Rendered inside Layout/ResearchLayout around just the routed page content,
 * so the sidebar (Settings, Refer a Friend, Roadmap, Contact Us, Logout)
 * always stays visible and usable, even while blocked. The Roadmap page
 * itself is also exempt from the block, since it's reachable straight from
 * the sidebar and isn't a paid feature.
 */
export default function BillingGate({
  children,
  requireActive = false,
}: Props) {
  const gatingEnabled =
    (import.meta.env.VITE_ENABLE_BILLING_GATING as string | undefined) ===
    "true";
  const [redirecting, setRedirecting] = useState(false);
  const [choosingFree, setChoosingFree] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [planType, setPlanType] = useState<"free" | "paid" | null>(null);
  const t = useT();
  const { intlLocale } = useLocale();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isRoadmap = location.pathname.endsWith("/roadmap");

  const { data: status, isLoading: loading } = useQuery({
    queryKey: ["billing-info-status"],
    queryFn: getBillingStatus,
    enabled: gatingEnabled,
  });

  if (!gatingEnabled || isRoadmap) {
    // Gating disabled, or an always-accessible route: always render children
    return <>{children}</>;
  }

  if (loading) {
    return null;
  }

  const sub = status?.subscription_status ?? null;
  const inGracePeriod = status?.trial_ends_at
    ? new Date(status.trial_ends_at).getTime() > Date.now()
    : false;
  const allowed =
    status?.is_ff === true ||
    (requireActive
      ? sub === "active" || inGracePeriod
      : sub === "active" || sub === "trialing" || inGracePeriod);

  // On the free tier the gate is a one-time question, not a wall: once the user has answered
  // it by staying free, they go straight through and per-feature limits take over.
  const freeTierEnabled = status?.free_tier_enabled === true;
  // A free user over the portfolio allowance picks which ones stay editable before going on.
  // Only once they have chosen the free plan: a user who never answered sees that question
  // first. This covers the first choice after the trial and any later return from paid.
  if (
    freeTierEnabled &&
    status?.free_tier_choice_at != null &&
    status.needs_portfolio_pick
  ) {
    return <FreeTierPortfolioPicker />;
  }
  if (allowed || (freeTierEnabled && status?.free_tier_choice_at != null)) {
    return <>{children}</>;
  }

  const handleChooseFree = async () => {
    if (choosingFree) return;
    setChoosingFree(true);
    try {
      await chooseFreeTier();
      await queryClient.invalidateQueries({
        queryKey: ["billing-info-status"],
      });
    } catch {
      toast.error(t("billing:toasts.freePlanFailed"), {
        description: t("billing:toasts.tryAgainShortly"),
      });
      setChoosingFree(false);
    }
  };

  const handleSubscribe = async () => {
    if (redirecting || !selectedPlan) return;
    setRedirecting(true);
    try {
      const url = await startTrial(selectedPlan);
      window.location.href = url;
    } catch {
      toast.error(t("billing:toasts.checkoutFailed"), {
        description: t("billing:toasts.tryAgainShortly"),
      });
      setRedirecting(false);
    }
  };

  const handleManageBilling = async () => {
    if (redirecting) return;
    setRedirecting(true);
    try {
      const url = await startBillingPortal();
      window.location.href = url;
    } catch {
      toast.error(t("billing:toasts.portalFailed"), {
        description: t("billing:toasts.tryAgainShortly"),
      });
      setRedirecting(false);
    }
  };

  // Reaching this point means trial_ends_at has already passed (that's the
  // block condition above) — so this is never a future prediction. The user
  // still needs to know their trial ended and what it costs now, regardless
  // of cohort — founding members additionally get the "why" (they signed up
  // during a fully free period with no pricing/trial expectations set).
  const trialEndDate = status?.trial_ends_at
    ? new Date(status.trial_ends_at).toLocaleDateString(intlLocale(undefined), {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  const isFounding = status?.is_founding_member ?? false;
  const prices = getPlanPrices(isFounding);
  const {
    monthly: monthlyPrice,
    yearly: yearlyPrice,
    yearlyList,
    onSale,
    yearlySavingsPercent,
    salePercentOff,
  } = prices;

  const pendingFreeMonths = status?.pending_free_months ?? 0;
  const { yearlyCredit, yearlyAfterCredit } = getReferralCredit(
    prices,
    pendingFreeMonths,
  );

  // Step 1: free or paid. Rendered on its own so the decision is not buried under the plan
  // pickers. Choosing paid falls through to the pre-existing subscribe card below, unchanged.
  if (freeTierEnabled && planType === null) {
    return (
      <div className="flex h-full min-h-[70vh] items-center justify-center">
        <Card className="max-w-xl w-full">
          <CardHeader>
            <CardTitle>{t("billing:trialEnded.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 text-sm">
            <p>
              {isFounding ? (
                <T
                  k="billing:choice.foundingEnded"
                  components={{ strong: <strong /> }}
                />
              ) : trialEndDate ? (
                t("billing:choice.trialEndedOn", { date: trialEndDate })
              ) : (
                t("billing:choice.trialEnded")
              )}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <ChoiceOption
                label={t("billing:choice.freeLabel")}
                price={t("billing:choice.freePrice")}
                priceNote={t("billing:choice.freeNote")}
                features={FREE_FEATURE_KEYS.map((key) => t(key))}
                selected={false}
                onSelect={handleChooseFree}
              />
              <ChoiceOption
                label={t("billing:choice.fullLabel")}
                price={t("billing:choice.fullPrice", {
                  monthly: monthlyPrice,
                  yearly: yearlyPrice,
                })}
                priceNote={
                  onSale
                    ? t("billing:choice.fullSaleNote", {
                        date: formatSaleEndDate(),
                        regular: yearlyList,
                      })
                    : t("billing:choice.fullNote")
                }
                saleBadge={
                  onSale
                    ? t("billing:plan.saleBadge", {
                        percent: salePercentOff,
                      })
                    : undefined
                }
                features={FULL_FEATURE_KEYS.map((key) => t(key))}
                selected={false}
                onSelect={() => setPlanType("paid")}
              />
            </div>

            {pendingFreeMonths > 0 && (
              <p className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-sm font-medium text-emerald-700">
                {t("billing:choice.banked", {
                  count: pendingFreeMonths,
                  yearly: formatFixed(yearlyAfterCredit, 2),
                  regular: yearlyPrice,
                })}
              </p>
            )}

            <p className="text-muted-foreground">
              {t("billing:choice.keepsData")}
            </p>

            <p className="text-muted-foreground">
              <T
                k="billing:trialEnded.contactSupport"
                components={{ contact: <ContactSupportLink /> }}
              />
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const referralCopy =
    selectedPlan === "yearly"
      ? t("billing:referral.copyYearly")
      : selectedPlan === "monthly"
        ? t("billing:referral.copyMonthly")
        : t("billing:referral.copyNone");

  const pendingCreditCopy =
    pendingFreeMonths > 0
      ? selectedPlan === "yearly"
        ? t("billing:referral.bankedYearly", {
            count: pendingFreeMonths,
            credit: formatFixed(yearlyCredit, 2),
            price: formatFixed(yearlyAfterCredit, 2),
          })
        : selectedPlan === "monthly"
          ? t("billing:referral.bankedMonthly", { count: pendingFreeMonths })
          : t("billing:referral.bankedNone", { count: pendingFreeMonths })
      : null;

  return (
    <div className="flex h-full min-h-[70vh] items-center justify-center">
      <Card className="max-w-xl w-full">
        <CardHeader>
          <CardTitle>{t("billing:trialEnded.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm">
          {isFounding ? (
            <p>
              <T
                k="billing:plan.foundingEnded"
                components={{ strong: <strong /> }}
              />
            </p>
          ) : (
            <p>
              {trialEndDate
                ? t("billing:plan.trialEndedOn", { date: trialEndDate })
                : t("billing:plan.trialEnded")}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <PlanOption
              label={t("billing:plan.monthly")}
              price={t("billing:plan.perMonth", { price: monthlyPrice })}
              subLabel={t("billing:plan.billedMonthly")}
              vatNote={t("billing:plan.vat")}
              selected={selectedPlan === "monthly"}
              onSelect={() => setSelectedPlan("monthly")}
            />
            <PlanOption
              label={t("billing:plan.yearly")}
              price={t("billing:plan.perYear", { price: yearlyPrice })}
              originalPrice={
                onSale
                  ? t("billing:plan.perYear", { price: yearlyList })
                  : undefined
              }
              subLabel={t("billing:plan.worksOutTo", {
                price: formatFixed(yearlyPrice / 12, 2),
              })}
              vatNote={t("billing:plan.vat")}
              badge={t("billing:plan.saveBadge", {
                percent: yearlySavingsPercent,
              })}
              saleBadge={
                onSale
                  ? t("billing:plan.saleBadge", {
                      percent: salePercentOff,
                    })
                  : undefined
              }
              selected={selectedPlan === "yearly"}
              onSelect={() => setSelectedPlan("yearly")}
            />
          </div>
          {onSale && (
            <p className="-mt-3 text-xs text-muted-foreground">
              {t("billing:plan.saleNote", {
                date: formatSaleEndDate(),
                price: yearlyList,
              })}
            </p>
          )}

          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
            <p className="flex items-center gap-1.5 text-xs font-bold tracking-[0.12em] uppercase text-primary mb-2">
              <Gift className="h-3.5 w-3.5" />
              {t("billing:referral.heading")}
              <HelpTooltip>
                <p>
                  <T
                    k="billing:referral.monthlyTip"
                    components={{ strong: <strong /> }}
                  />
                </p>
                <p>
                  <T
                    k="billing:referral.yearlyTip"
                    components={{ strong: <strong /> }}
                  />
                </p>
              </HelpTooltip>
            </p>
            <p className="text-sm text-foreground/80 leading-relaxed min-h-[4.875em]">
              {referralCopy}
            </p>
            <ReferralModal>
              <button className="mt-2 text-xs font-medium text-primary underline underline-offset-2 hover:no-underline">
                {t("billing:referral.getInviteLink")}
              </button>
            </ReferralModal>

            {pendingCreditCopy && (
              <p className="mt-3 pt-3 border-t border-primary/20 text-sm font-medium text-emerald-700">
                {pendingCreditCopy}
              </p>
            )}
          </div>

          <p className="text-muted-foreground">
            <T
              k="billing:trialEnded.contactSupport"
              components={{ contact: <ContactSupportLink /> }}
            />
          </p>
          <div className="flex justify-end gap-2 pt-2">
            {freeTierEnabled && (
              <Button
                variant="outline"
                onClick={() => setPlanType(null)}
                disabled={redirecting}
              >
                {t("common:actions.back")}
              </Button>
            )}
            <Button
              onClick={handleSubscribe}
              disabled={redirecting || !selectedPlan}
            >
              {t("billing:actions.subscribe")}
            </Button>
            {status?.stripe_customer_id && (
              <Button
                variant="outline"
                onClick={handleManageBilling}
                disabled={redirecting}
              >
                {t("billing:actions.manageBilling")}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("billing:stripeNotice")}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
